import { FC } from "react";

import { useRecoilState, useRecoilValue } from "recoil";

import { Dialog } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { ShopItemInBugo } from "@shared/api/shopItem/shopItem.interface";
import {
  getShopItemDisplayName,
  getShopItemImageUrl,
  getShopItemName,
} from "@shared/api/shopItem/shopItem.utils";
import { useModalHook } from "@shared/hooks/useModalHook";
import { simpleOpenAtomFamily } from "@shared/state/atom";
import { customThemeAtom } from "@shared/state/atom/theme.atom";
import { Link } from "react-router-dom";

interface Props {
  shopItem?: ShopItemInBugo;
  bugoId: string;
  openId: string;
}

export const SameItemBuyModal: FC<Props> = function SameItemBuyModal({
  shopItem,
  bugoId,
  openId,
}: Props) {
  const [open, setOpen] = useRecoilState(simpleOpenAtomFamily(openId));
  const customTheme = useRecoilValue(customThemeAtom);
  useModalHook(openId);
  const onCloseModal = () => {
    setOpen(false);
    history.back();
  };
  if (shopItem) {
    const shopItemImageUrl = getShopItemImageUrl(shopItem);
    const name = getShopItemName(shopItem);
    const displayName = getShopItemDisplayName(shopItem);

    return (
      <Dialog
        open={open}
        onClose={onCloseModal}
        className={`${customTheme} fixed inset-0 z-40`}
      >
        <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-3 sm:p-6">
            <Dialog.Panel className="w-full max-w-[360px] rounded-2xl bg-white p-4 shadow-xl">
              <div className="mb-3 flex items-center justify-between gap-2">
                <Dialog.Title className="text-16 break-keep font-bold leading-6 text-gray-800">
                  동일한 상품으로 마음을 전하시겠습니까?
                </Dialog.Title>
                <button
                  type="button"
                  aria-label="동일 상품 구매 닫기"
                  onClick={onCloseModal}
                  className="-mr-2 -mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                >
                  <XIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <div className="flex items-center gap-3">
                <div className="h-28 w-28 shrink-0 overflow-hidden rounded-lg">
                  <img
                    src={`${process.env.REACT_APP_BUCKET_URL}/w400/${shopItemImageUrl}`}
                    alt={name || "상품이미지"}
                    className="h-full w-full object-contain"
                  />
                </div>
                <p className="text-17 min-w-0 flex-1 break-keep font-semibold leading-7 text-gray-800">
                  {displayName}
                </p>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className="text-15 min-h-[44px] rounded-lg border border-gray-200 bg-white px-4 font-medium text-gray-600 hover:bg-gray-50"
                  onClick={onCloseModal}
                >
                  취소
                </button>
                <Link
                  to={`/flower-store/item/${shopItem._id}/checkout/?bugoId=${bugoId}`}
                  className="text-15 theme-bg-main flex min-h-[44px] items-center justify-center rounded-lg px-4 font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
                >
                  확인
                </Link>
              </div>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    );
  } else {
    return null;
  }
};
